import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Stack, Chip, Button, Divider
} from '@mui/material';
import { blueGrey, teal, green, lightGreen, grey } from '@mui/material/colors';
import { motion, useScroll, useTransform } from 'framer-motion';
import RecyclingIcon from '@mui/icons-material/Recycling';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BoltIcon from '@mui/icons-material/Bolt';
import Co2Icon from '@mui/icons-material/Co2';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

function ScrollReveal({ children, delay = 0, direction = 'up' }) {
  const variants = {
    up: { hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0, x: 0 } },
    left: { hidden: { opacity: 0, x: -50 }, visible: { opacity: 1, y: 0, x: 0 } },
    right: { hidden: { opacity: 0, x: 50 }, visible: { opacity: 1, y: 0, x: 0 } },
  };
  return (
    <motion.div
      variants={variants[direction]}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </motion.div>
  );
}

const STATS = [
  { icon: <Co2Icon sx={{ fontSize: 28, color: '#66bb6a' }} />, value: '500g', label: 'CO₂ 절감', sub: '세척 1회당' },
  { icon: <WaterDropIcon sx={{ fontSize: 28, color: '#29b6f6' }} />, value: '98%', label: '물 절약', sub: '일회용 컵 대비' },
  { icon: <BoltIcon sx={{ fontSize: 28, color: '#ffb300' }} />, value: '60%', label: '전기 절약', sub: '기존 세척 대비' },
  { icon: <RecyclingIcon sx={{ fontSize: 28, color: '#a5d6a7' }} />, value: '100%', label: '재사용 가능', sub: '텀블러 기반' },
];

const COMMITMENTS = [
  {
    icon: <EmojiNatureIcon sx={{ fontSize: 36, color: green[600] }} />,
    title: '친환경 하드웨어',
    description: '텀블러 세척기와 청소카트는 일회용 소비를 줄이기 위해 설계되었습니다. 제품 수명주기 전반에서 탄소 발자국을 최소화하며, 지속 가능한 소재를 적극 활용합니다.',
    color: green[600],
  },
  {
    icon: <Co2Icon sx={{ fontSize: 36, color: teal[600] }} />,
    title: '데이터 기반 탄소 관리',
    description: 'ENV-REPORT를 통해 세척기 사용 데이터를 실시간 수집하고 탄소·물·전기 절감량을 정확하게 측정합니다. 측정된 데이터는 ESG 보고서와 탄소 감축 인증에 직접 활용됩니다.',
    color: teal[600],
  },
  {
    icon: <HandshakeIcon sx={{ fontSize: 36, color: lightGreen[700] }} />,
    title: 'ESG 연계 프로그램',
    description: 'ENV-AD를 통해 광고 집행 금액의 일부를 탄소 크레딧 구매나 환경 캠페인에 연계합니다. 광고 자체가 환경에 기여하는 임팩트 마케팅을 실현합니다.',
    color: lightGreen[700],
  },
];

const GOALS = [
  { year: '2025', title: '탄소중립마켓(VCM) 런칭', desc: '자발적 탄소 크레딧 거래 플랫폼 운영 시작' },
  { year: '2026', title: '전국 설치 1,000대 달성', desc: '텀블러 세척기 전국 1,000대 설치 및 ENV-REPORT 누적 데이터 공개' },
  { year: '2027', title: 'Net-Zero 파트너십', desc: '100개 이상의 기업과 탄소중립 파트너십 체결' },
  { year: '2030', title: '100만 톤 CO₂ 감축', desc: '다끌레어 네트워크 누적 탄소 감축량 100만 톤 달성' },
];

function SustainabilityPage() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <Box>
      {/* ───── 히어로 섹션 ───── */}
      <Box
        ref={ref}
        component="section"
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #0a1a0a 0%, #1b3a1b 50%, #0d2a0d 100%)',
        }}
      >
        {/* 배경 패턴 */}
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(76,175,80,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(76,175,80,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(ellipse 70% 60% at 50% 40%, rgba(76,175,80,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <Box
          component={motion.div}
          style={{ y: textY, opacity: bgOpacity }}
          sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: { xs: 3, md: 6 }, maxWidth: '860px', width: '100%', py: { xs: 12, md: 0 } }}
        >
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <Chip
              label="SUSTAINABILITY"
              size="small"
              sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '11px', letterSpacing: '3px', backgroundColor: 'rgba(76,175,80,0.15)', color: '#a5d6a7', border: '1px solid rgba(76,175,80,0.3)', mb: 3 }}
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '30px', sm: '44px', md: '58px' }, color: '#ffffff', lineHeight: 1.15, letterSpacing: { xs: '-1px', md: '-2px' }, mb: 3 }}>
              지속 가능한 미래를 위한<br />
              <Box component="span" sx={{ background: 'linear-gradient(90deg, #a5d6a7, #66bb6a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                우리의 약속
              </Box>
            </Typography>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }}>
            <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 300, fontSize: { xs: '15px', md: '18px' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.85, mb: 6, maxWidth: '600px', mx: 'auto' }}>
              다끌레어는 지구와 사람을 위한 지속 가능한 솔루션을 제공합니다.<br />
              친환경 하드웨어, 데이터 기반 탄소 관리, ESG 연계 프로그램으로<br />
              보다 나은 세상을 만들기 위해 노력합니다.
            </Typography>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.75 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => navigate('/services/env-report')}
                endIcon={<ArrowForwardIcon />}
                sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '15px', px: 4, py: 1.6, borderRadius: '50px', backgroundColor: '#2e7d32', boxShadow: '0 0 30px rgba(46,125,50,0.4)', '&:hover': { backgroundColor: '#1b5e20', boxShadow: '0 0 40px rgba(46,125,50,0.6)' } }}
              >
                ENV-REPORT 알아보기
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/company/contact')}
                sx={{ fontFamily: 'NotoSansKR', fontWeight: 600, fontSize: '15px', px: 4, py: 1.6, borderRadius: '50px', color: 'rgba(255,255,255,0.8)', borderColor: 'rgba(255,255,255,0.25)', '&:hover': { borderColor: 'rgba(255,255,255,0.6)', backgroundColor: 'rgba(255,255,255,0.06)' } }}
              >
                파트너십 문의
              </Button>
            </Stack>
          </motion.div>
        </Box>

        {/* 스크롤 인디케이터 */}
        <Box
          component={motion.div}
          style={{ opacity: bgOpacity }}
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          sx={{ position: 'absolute', bottom: '36px', left: '50%', transform: 'translateX(-50%)', display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', gap: '4px', zIndex: 2 }}
        >
          <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '11px', color: 'rgba(255,255,255,0.25)', letterSpacing: '3px', textTransform: 'uppercase' }}>Scroll</Typography>
          <KeyboardArrowDownIcon sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '20px' }} />
        </Box>
      </Box>

      {/* ───── 환경 성과 지표 ───── */}
      <Box sx={{ backgroundColor: '#f1f8e9', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <ScrollReveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 7, md: 10 } }}>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '26px', md: '38px' }, color: blueGrey[900], mb: 1.5 }}>
                숫자로 증명하는 환경 기여
              </Typography>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: { xs: '15px', md: '17px' }, color: blueGrey[500], lineHeight: 1.8 }}>
                다끌레어 하드웨어가 만들어내는 실질적인 환경 변화
              </Typography>
            </Box>
          </ScrollReveal>

          <Grid container spacing={3}>
            {STATS.map((stat, i) => (
              <Grid size={{xs:6, md:3}} key={stat.label}>
                <ScrollReveal delay={i * 0.1}>
                  <Box sx={{ textAlign: 'center', p: 4, borderRadius: '20px', backgroundColor: '#ffffff', border: '1px solid #e8f5e9', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 40px rgba(46,125,50,0.12)' } }}>
                    <Box sx={{ mb: 2 }}>{stat.icon}</Box>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '28px', md: '36px' }, color: blueGrey[900], lineHeight: 1 }}>
                      {stat.value}
                    </Typography>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '14px', color: blueGrey[700], mt: 0.5 }}>
                      {stat.label}
                    </Typography>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '12px', color: blueGrey[400], mt: 0.5 }}>
                      {stat.sub}
                    </Typography>
                  </Box>
                </ScrollReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ───── 지속가능성 약속 ───── */}
      <Box sx={{ backgroundColor: '#ffffff', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <ScrollReveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 7, md: 10 } }}>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '26px', md: '38px' }, color: blueGrey[900], mb: 1.5 }}>
                세 가지 지속가능성 약속
              </Typography>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: { xs: '15px', md: '17px' }, color: blueGrey[500], lineHeight: 1.8 }}>
                다끌레어가 환경을 위해 실천하는 구체적인 방법
              </Typography>
            </Box>
          </ScrollReveal>

          <Grid container spacing={4}>
            {COMMITMENTS.map((item, i) => (
              <Grid size={{xs:12, md:4}} key={item.title}>
                <ScrollReveal delay={i * 0.15}>
                  <Box sx={{ p: 4, borderRadius: '24px', border: '1px solid #eeeeee', height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 20px 60px rgba(0,0,0,0.08)', transform: 'translateY(-6px)', borderColor: `${item.color}40` } }}>
                    <Box sx={{ width: 72, height: 72, borderRadius: '20px', backgroundColor: `${item.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                      {item.icon}
                    </Box>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '20px', color: blueGrey[900], mb: 2 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '14px', color: blueGrey[600], lineHeight: 1.85, flex: 1 }}>
                      {item.description}
                    </Typography>
                  </Box>
                </ScrollReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ───── 로드맵 ───── */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: { xs: 10, md: 14 } }}>
        <Container maxWidth="lg">
          <ScrollReveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 7, md: 10 } }}>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '26px', md: '38px' }, color: blueGrey[900], mb: 1.5 }}>
                지속가능성 로드맵
              </Typography>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: { xs: '15px', md: '17px' }, color: blueGrey[500], lineHeight: 1.8 }}>
                다끌레어가 그리는 2030년까지의 환경 목표
              </Typography>
            </Box>
          </ScrollReveal>

          <Grid container spacing={3}>
            {GOALS.map((goal, i) => (
              <Grid size={{xs:12, sm:6, md:3}} key={goal.year}>
                <ScrollReveal delay={i * 0.12}>
                  <Box sx={{ p: 3.5, borderRadius: '20px', backgroundColor: i === GOALS.length - 1 ? '#1b5e20' : '#ffffff', border: `1px solid ${i === GOALS.length - 1 ? '#1b5e20' : '#e8f5e9'}`, height: '100%', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(0,0,0,0.1)' } }}>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '28px', color: i === GOALS.length - 1 ? '#a5d6a7' : green[600], mb: 1 }}>
                      {goal.year}
                    </Typography>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '16px', color: i === GOALS.length - 1 ? '#ffffff' : blueGrey[900], mb: 1.5 }}>
                      {goal.title}
                    </Typography>
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 400, fontSize: '13px', color: i === GOALS.length - 1 ? 'rgba(255,255,255,0.7)' : blueGrey[600], lineHeight: 1.7 }}>
                      {goal.desc}
                    </Typography>
                  </Box>
                </ScrollReveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ───── CTA ───── */}
      <Box sx={{ background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 50%, #388e3c 100%)', py: { xs: 12, md: 16 } }}>
        <Container maxWidth="md">
          <ScrollReveal>
            <Box sx={{ textAlign: 'center' }}>
              <NaturePeopleIcon sx={{ fontSize: 52, color: 'rgba(255,255,255,0.4)', mb: 2 }} />
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: { xs: '26px', md: '38px' }, color: '#ffffff', mb: 2, lineHeight: 1.3 }}>
                다끌레어에 대해<br />더 많은 정보를 알고 싶다면
              </Typography>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 300, fontSize: { xs: '15px', md: '17px' }, color: 'rgba(255,255,255,0.65)', lineHeight: 1.85, mb: 5, maxWidth: '480px', mx: 'auto' }}>
                우리의 미션과 ESG 전략에 대해 더 자세히 알아보거나<br />파트너십을 논의하고 싶으신가요?
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => navigate('/company/contact')}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '15px', px: 4, py: 1.6, borderRadius: '50px', backgroundColor: '#ffffff', color: '#1b5e20', '&:hover': { backgroundColor: '#f1f8e9', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' } }}
                >
                  문의하기
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/company/mission')}
                  sx={{ fontFamily: 'NotoSansKR', fontWeight: 600, fontSize: '15px', px: 4, py: 1.6, borderRadius: '50px', color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.08)' } }}
                >
                  미션 보기
                </Button>
              </Stack>
            </Box>
          </ScrollReveal>
        </Container>
      </Box>
    </Box>
  );
}

export default SustainabilityPage;
